import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  InputGroup,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import DeleteModal from "../../../Components/Common/DeleteModal";
import AdvertisersService from "../../../services/OrderingServices/AdvertisersService";
import AdvertiserFilterCard from "../component/AdvertiserFilterCard";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const AdvertisersList = () => {
  const navigate = useNavigate();
  const [advertisersList, setAdvertisersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  console.log("🚀 ~ AdvertisersList ~ isOpenDelete:", isOpenDelete);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [filters, setFilters] = useState({
    advertiserName: "",
    advertiserEmail: "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const toggleDelete = () => {
    setIsOpenDelete((prev) => !prev);
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    fetchAdvertisersList();
  }, [currentPage, pageSize, debouncedFilters]);

  async function fetchAdvertisersList(
    page = currentPage,
    size = pageSize,
    currentFilters = debouncedFilters,
  ) {
    try {
      setLoading(true);
      const response = await AdvertisersService.advertisersList({
        advertiserName: currentFilters.advertiserName,
        advertiserEmail: currentFilters.advertiserEmail,
        page,
        pageSize: size,
      });

      if (response) {
        setAdvertisersList(Array.isArray(response) ? response : response || []);
        setTotalRows(response.totalCount || response.length || 0);
      }
    } catch (error) {
      toast.error("Failed to fetch advertisers");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      advertiserName: "",
      advertiserEmail: "",
    });
    setCurrentPage(1);
  };

  const handleDelete = async () => {
    try {
      if (selectedAdvertiser && selectedAdvertiser.id) {
        await AdvertisersService.delete(selectedAdvertiser.id);
        toast.success("Advertiser deleted successfully");
        toggleDelete();
        fetchAdvertisersList();
      }
    } catch (error) {
      toast.error("Failed to delete advertiser");
      console.error(error);
    }
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Advertiser Name",
      selector: (row) => row.advertiserName,
      cell: (row) => (
        <Button
          color="link"
          className="p-0"
          onClick={() => navigate(`/advertiser/view/${row.id}`)}
        >
          {row.advertiserName}
        </Button>
      ),
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.advertiserEmail,
      sortable: true,
    },
    {
      name: "Contact",
      selector: (row) => row.advertiserContact || "—",
      sortable: true,
    },
    {
      name: "Location",
      selector: (row) => row.advertiserLocation || "—",
      sortable: true,
    },
    {
      name: "Contact Person",
      selector: (row) => row.contactPerson || "—",
      sortable: true,
    },
    {
      name: "Contact Phone",
      selector: (row) => row.contactPhone || "—",
      sortable: true,
    },
    {
      name: "Action",
      selector: (row) => (
        <div className="d-flex gap-2">
          <Button
            color="btn btn-soft-info"
            className="btn-sm"
            onClick={() => navigate(`/advertiser/view/${row.id}`)}
            title="View Details"
          >
            <i className="ri-eye-line" />
          </Button>
          <Button
            color="btn btn-soft-primary"
            className="btn-sm"
            onClick={() => navigate(`/advertiser/${row.id}`)}
            title="Edit"
          >
            <i className="ri-edit-line" />
          </Button>
          <Button
            className="btn btn-soft-danger btn-sm"
            onClick={() => {
              console.log("Delete clicked", row); // Debug
              setSelectedAdvertiser(row);
              toggleDelete();
            }}
            title="Delete"
          >
            <i className="ri-delete-bin-5-line" />
          </Button>
        </div>
      ),
      center: true,
      width: "140px",
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb title="Advertiser List" pageTitle="Advertisers" />
        </div>
        <Container fluid>
          {/* Filter Card */}
          <AdvertiserFilterCard
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleClearFilters={handleClearFilters}
            setCurrentPage={setCurrentPage}
            onAddNewClick={() => navigate("/advertiser/new")}
          />

          {/* Data Table */}
          <Card className="border-0 shadow-sm">
            <CardBody>
              <CustomDataTable
                responsive
                striped
                pagination
                fixedHeader
                persistTableHead
                progressPending={loading}
                customStyles={customStyles}
                progressComponent={
                  <div className="my-3 text-center">
                    <Triangle
                      visible={true}
                      height="80"
                      width="80"
                      color="#435ebe"
                      ariaLabel="triangle-loading"
                    />
                    <h5 className="mt-2">Loading...</h5>
                  </div>
                }
                columns={columns}
                data={advertisersList}
                noDataComponent={
                  <div className="py-4 text-center">
                    <p className="text-muted">No advertisers found</p>
                  </div>
                }
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={isOpenDelete}
        toggle={toggleDelete}
        onConfirm={handleDelete}
        title="Delete Advertiser"
        body={`Are you sure you want to delete "${selectedAdvertiser?.advertiserName}"?`}
      />
    </React.Fragment>
  );
};

export default AdvertisersList;
